import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import type { Truck, TruckSearchFilters } from "../../types/truck";
import { searchTrucksApi } from "../../api/trucksApi";
import type { RootState } from "../index";
import type { PaginationResponse, PaginationParam } from "../../types/paginationResponse";

interface RetiredTruckState {
  trucks: Truck[];
  loading: boolean;
  error: string | null;
  pagination: PaginationResponse | null;
}

const initialState: RetiredTruckState = {
  trucks: [],
  loading: false,
  error: null,
  pagination: null,
};

export const searchRetiredTrucks = createAsyncThunk<
  { trucks: Truck[]; pagination: PaginationResponse },
  { filters?: TruckSearchFilters; pagination?: PaginationParam },
  { rejectValue: string }
>("retiredTrucks/searchRetiredTrucks", async ({ filters, pagination }, { rejectWithValue }) => {
  try {
    return await searchTrucksApi(filters, pagination, "only");
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || "Failed to search retired trucks");
  }
});

const retiredTruckSlice = createSlice({
  name: "retiredTrucks",
  initialState,
  reducers: {
    clearRetiredTrucksError: (state) => {
      state.error = null;
    },
    updateRetiredTruck: (state, action: PayloadAction<Truck>) => {
      const index = state.trucks.findIndex((truck) => truck._id === action.payload._id);
      if (index !== -1) {
        state.trucks[index] = action.payload;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(searchRetiredTrucks.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(searchRetiredTrucks.fulfilled, (state, action) => {
        state.loading = false;
        state.trucks = action.payload.trucks;
        state.pagination = action.payload.pagination;
      })
      .addCase(searchRetiredTrucks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to search retired trucks";
      });
  },
});

export const { clearRetiredTrucksError, updateRetiredTruck } = retiredTruckSlice.actions;

// Selectors
export const selectRetiredTrucks = (state: RootState) => state.retiredTrucks.trucks;
export const selectRetiredTrucksLoading = (state: RootState) => state.retiredTrucks.loading;
export const selectRetiredTrucksError = (state: RootState) => state.retiredTrucks.error;
export const selectRetiredTrucksPagination = (state: RootState) => state.retiredTrucks.pagination;

export default retiredTruckSlice.reducer;
